import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchOpsReviewList,
  fetchOpsReviewDetail,
  fetchOpsReviewRange,
  triggerOpsReview,
} from '@/lib/api/ops-reviews'

export const opsReviewKeys = {
  all: ['ops-reviews'] as const,
  list: () => [...opsReviewKeys.all, 'list'] as const,
  detail: (date?: string) => [...opsReviewKeys.all, 'detail', date ?? 'latest'] as const,
  range: (from: string, to: string) => [...opsReviewKeys.all, 'range', from, to] as const,
}

export function useOpsReviewList() {
  return useQuery({
    queryKey: opsReviewKeys.list(),
    queryFn: () => fetchOpsReviewList(),
    staleTime: 60 * 1000,
  })
}

export function useOpsReviewDetail(reviewDate?: string) {
  return useQuery({
    queryKey: opsReviewKeys.detail(reviewDate),
    queryFn: () => fetchOpsReviewDetail(reviewDate),
    staleTime: 60 * 1000,
  })
}

export function useOpsReviewRange(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: opsReviewKeys.range(from, to),
    queryFn: () => fetchOpsReviewRange(from, to),
    enabled: enabled && !!from && !!to,
    staleTime: 60 * 1000,
  })
}

export function useTriggerOpsReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ window, notify }: { window?: string; notify?: boolean }) =>
      triggerOpsReview(window, notify),
    onSuccess: () => {
      // The run is async (202 + background) — refetch shortly so the new row lands.
      setTimeout(() => qc.invalidateQueries({ queryKey: opsReviewKeys.all }), 8000)
    },
  })
}
